import { State } from '@progress/kendo-data-query';
import { GridQueryUtils } from '../utility/grid-query-utils';
import {
  clampKeysetPageSize,
  KeysetNavigationDirection,
  KeysetPagingMeta
} from './keyset-paging-meta';
import { KeysetSortFields, KeysetSortStrategy } from './keyset-paging-sort';
import { KeysetPagingSession } from './keyset-paging-session';

export interface BuildKeysetSearchBodyParams
{
  search: string;
  size: number;
  navigationDirection: KeysetNavigationDirection;
  cursor?: any | null;
  sort?: KeysetSortFields;
  /** Merged last (e.g. {@code recentOnly}, feature flags). */
  extra?: Record<string, any>;
  maxPageSize?: number;
}

export type KeysetFirstPageIntent =
  | { type: 'use-cache'; virtualSkip: number }
  | {
      type: 'fetch-first';
      navigationDirection: KeysetNavigationDirection;
      cursor: null;
      size: number;
      meta: KeysetPagingMeta;
    };

/** Builds a JSON body for keyset search POSTs. */
export function buildKeysetSearchBody(params: BuildKeysetSearchBodyParams): Record<string, any>
{
  const body: Record<string, any> = {
    search: params.search || '',
    size: clampKeysetPageSize(params.size, params.maxPageSize),
    navigationDirection: params.navigationDirection,
    ...(params.extra ?? {})
  };
  const sort = params.sort;
  if (sort?.sortProperty)
  {
    body.sortProperty = sort.sortProperty;
    body.sortDirection = sort.sortDirection;
  }
  if (params.cursor != null)
  {
    body.cursor = params.cursor;
  }
  return body;
}

/** Builds the keyset POST body from grid state, sort strategy, and API extras. */
export function buildKeysetSearchBodyFromGridState<TContext>(
  state: State,
  strategy: KeysetSortStrategy<TContext>,
  sortContext: TContext,
  navigationDirection: KeysetNavigationDirection,
  cursor: any | null,
  size: number,
  extra: Record<string, any>,
  maxPageSize?: number
): Record<string, any>
{
  const search = GridQueryUtils.buildSearchQueryParameter(state, false);
  const sort = strategy.resolveSort(state, sortContext);
  return buildKeysetSearchBody({
    search: search || '',
    size,
    navigationDirection,
    cursor,
    sort,
    extra,
    maxPageSize
  });
}

/** Anchor de-dupe cache vs issuing a FIRST keyset request. */
export function resolveKeysetFirstPageIntent(
  session: KeysetPagingSession,
  state: State,
  anchorExtras: Record<string, any>,
  force: boolean,
  maxPageSize?: number
): KeysetFirstPageIntent
{
  const anchor = session.computeAnchorFingerprint(state, anchorExtras);
  if (session.shouldUseCachedFirstPageWithoutRefetch(anchor, force))
  {
    return { type: 'use-cache', virtualSkip: session.virtualSkipValue };
  }
  session.commitAnchorForIssuedFetch(anchor);
  const size = clampKeysetPageSize(state.take, maxPageSize);
  return {
    type: 'fetch-first',
    navigationDirection: 'NEXT',
    cursor: null,
    size,
    meta: {
      requestNavigationDirection: 'NEXT',
      requestSize: size,
      hadRequestCursor: false,
      requestMode: 'FIRST'
    }
  };
}

/** Applies {@link resolveKeysetFirstPageIntent}: sync virtual skip or issue a FIRST keyset fetch. */
export function applyKeysetFirstPageIntent(
  intent: KeysetFirstPageIntent,
  onFetch: (navigationDirection: KeysetNavigationDirection, cursor: any | null, size: number, meta: KeysetPagingMeta) => void,
  onUseCache: (virtualSkip: number) => void
): void
{
  if (intent.type === 'use-cache')
  {
    onUseCache(intent.virtualSkip);
    return;
  }
  onFetch(intent.navigationDirection, intent.cursor, intent.size, intent.meta);
}

/** Build POST body and delegate to the grid service keyset method. */
export function runKeysetGridSearch(
  buildBody: (navigationDirection: KeysetNavigationDirection, cursor: any | null, size: number) => Record<string, any>,
  postKeyset: (body: Record<string, any>, meta: KeysetPagingMeta) => void,
  navigationDirection: KeysetNavigationDirection,
  cursor: any | null,
  size: number,
  meta: KeysetPagingMeta
): void
{
  const body = buildBody(navigationDirection, cursor, size);
  postKeyset(body, meta);
}
